import React, { useState, useRef, useEffect, createContext, useContext, ReactNode } from 'react'

// ---------------- CONTEXT ----------------
type TabsContextType = {
  value: number
  setValue: (v: number) => void
}

const TabsContext = createContext<TabsContextType | null>(null)

// ---------------- ROOT TABS ----------------
export function Tabs({
  value: controlled,
  defaultValue = 0,
  onChange,
  children,
}: {
  value?: number
  defaultValue?: number
  onChange?: (v: number) => void
  children: ReactNode
}) {
  const isControlled = controlled !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue)

  const value = isControlled ? controlled! : internalValue

  const setValue = (v: number) => {
    if (!isControlled) setInternalValue(v)
    onChange?.(v)
  }

  return <TabsContext.Provider value={{ value, setValue }}>{children}</TabsContext.Provider>
}

// ---------------- TAB LIST ----------------
export function TabList({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabList must be inside <Tabs>')

  const listRef = useRef<HTMLDivElement | null>(null)
  const indicatorRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<HTMLButtonElement[]>([])

  const updateIndicator = () => {
    const active = tabRefs.current[ctx.value]
    const list = listRef.current
    const ind = indicatorRef.current
    if (!active || !list || !ind) return

    const listRect = list.getBoundingClientRect()
    const activeRect = active.getBoundingClientRect()

    ind.style.width = `${activeRect.width}px`
    ind.style.transform = `translateX(${activeRect.right - listRect.right}px)`
  }

  useEffect(() => updateIndicator())
  useEffect(() => {
    window.addEventListener('resize', updateIndicator)
    return () => window.removeEventListener('resize', updateIndicator)
  }, [])

  return (
    <div
      ref={listRef}
      style={{ borderBottomWidth: 1 }}
      className={`relative border-b border-gray-300 ${className}`}
    >
      <div className="flex w-full gap-4">
        {React.Children.map(children, (child, i) => {
          if (!React.isValidElement(child)) return child
          return React.cloneElement(child, {
            tabIndexInternal: i,
            tabRef: (el: HTMLButtonElement) => (tabRefs.current[i] = el),
          } as any)
        })}
      </div>

      {/* Indicator */}
      <div
        ref={indicatorRef}
        className="absolute bottom-0 transition-all duration-200"
        style={{
          width: 0,
          height: 2,
          transform: 'translateX(0)',
          backgroundColor: 'rgb(28 100 242 / var(--tw-bg-opacity, 1))',
        }}
      />
    </div>
  )
}

// ---------------- TAB ----------------
export function Tab({
  children,
  tabIndexInternal,
  disabled,
  tabRef,
}: {
  children?: ReactNode
  tabIndexInternal?: number
  disabled?: boolean
  tabRef?: (el: HTMLButtonElement) => void
}) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('Tab must be inside <Tabs>')

  const active = ctx.value === tabIndexInternal
  const ref = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (ref.current && tabRef) tabRef(ref.current)
  }, [ref.current])

  return (
    <button
      ref={ref}
      onClick={() => ctx.setValue(tabIndexInternal ?? 0)}
      className={`flex-1 py-2 px-1 text-sm font-semibold transition-colors ${
        active ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'
      }`}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

// ---------------- PANEL ----------------
export function TabPanel({
  value,
  children,
  className = '',
}: {
  value: number
  children: ReactNode
  className?: string
}) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabPanel must be inside <Tabs>')

  return ctx.value === value ? <div className={`py-4 ${className}`}>{children}</div> : null
}
